import {
  sendScheduleChangeNotification,
  type ScheduleNotificationChoice,
} from '@/hooks/jobs/useScheduleChangeNotification';
import type { CalendarArrivalWindowChange } from '@/lib/scheduler/calendarArrivalWindowChange';
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiPatch } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, jobKeys, schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerBooking } from '@/types/scheduler';

/**
 * NOT-317 — calendar drag-to-schedule: set an unassigned booking's start time
 * without a technician (no slot reservation / conflict gate). Distinct from
 * `useMoveBooking`, which is the Dispatch board's per-technician move.
 */
export interface ScheduleBookingDto {
  notification?: ScheduleNotificationChoice;
  arrivalWindowChange?: CalendarArrivalWindowChange;
  startAt: string;
  endAt?: string;
  /** Staff acknowledged the conflicts the engine reported for this time. */
  overrideConflicts?: boolean;
  manualScheduling?: boolean;
}

export type ScheduleBookingVariables = ScheduleBookingDto & { id: string };

export function useScheduleBooking() {
  const queryClient = useQueryClient();

  return useApiMutation<SchedulerBooking, ScheduleBookingVariables>({
    mutationFn: async ({
      id,
      startAt,
      endAt,
      overrideConflicts,
      manualScheduling,
      arrivalWindowChange,
      notification: moveNotification,
    }) => {
      const { notification, ...windowChange } = arrivalWindowChange ?? {};
      const updated = await apiPatch<SchedulerBooking, ScheduleBookingDto>(
        operationsClient,
        schedulerEndpoints.schedule(id),
        {
          startAt,
          // NOT-329 — endAt only when resizing; omitted otherwise (preserve duration).
          ...(endAt ? { endAt } : {}),
          ...(manualScheduling ? { manualScheduling: true } : {}),
          ...(arrivalWindowChange
            ? { arrivalWindowChange: windowChange as CalendarArrivalWindowChange }
            : {}),
          ...(overrideConflicts ? { overrideConflicts: true } : {}),
        },
      );
      await sendScheduleChangeNotification(
        updated.appointmentId ?? id,
        notification ?? moveNotification,
        updated.arrivalWindow ??
          (updated.promise?.startAt && updated.promise.endAt
            ? { start: updated.promise.startAt, end: updated.promise.endAt }
            : null),
      );
      return updated;
    },
    // Errors (incl. the 409 conflict handshake) are the caller's: the calendar
    // routes them through useConflictOverride and toasts anything else itself.
    showErrorToast: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      // The visit that moved belongs to a job, and the job's own views read it
      // through their own cache: without this the visit panel goes on showing
      // the time and arrival window the visit had before the move.
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
    },
  });
}
